import { TentMark } from "../components/TentMark.js";
import { nl } from "../copy/nl.js";

// The user's own embed from Google Maps, pinned to the place ID of
// "Chirolokalen Sint-Jozef Rijkevorsel" — a text query showed the rival chiro
// next to ours. Loads on render (the user's call, twice); if a cookie banner
// ever becomes a topic, this embed is the only thing on the site needing it.
const MAPS_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d25884.659919835507!2d4.77634775!3d51.3404709!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c6abfbe643aa2b%3A0xdcd66bd3bf5b49ea!2sChirolokalen%20Sint-Jozef%20Rijkevorsel!5e1!3m2!1sen!2sbe!4v1787084725956!5m2!1sen!2sbe";
const MAPS_URL = "https://maps.app.goo.gl/RDHrvkG3Q4GBduns8";

function Plattegrond() {
  return (
    <svg
      viewBox="0 0 440 200"
      role="img"
      aria-label={nl.praktisch.gebouw.plan.label}
      className="w-full"
    >
      <g fontFamily="Spline Sans Mono Variable, monospace" fontSize="9.5" fill="#4a5347">
        <rect
          x="12"
          y="26"
          width="256"
          height="160"
          fill="#fbf9f2"
          stroke="#2f4a38"
          strokeWidth="1.8"
        />
        <rect
          x="12"
          y="26"
          width="160"
          height="160"
          fill="none"
          stroke="#2f4a38"
          strokeWidth="1.2"
        />
        <text x="52" y="102" fontSize="11" fontWeight="600">
          {nl.praktisch.gebouw.plan.zaal}
        </text>
        <text x="62" y="116">
          {nl.praktisch.gebouw.plan.zaalOppervlakte}
        </text>
        <rect
          x="172"
          y="26"
          width="96"
          height="70"
          fill="none"
          stroke="#2f4a38"
          strokeWidth="1.2"
        />
        <text x="196" y="64">
          {nl.praktisch.gebouw.plan.keuken}
        </text>
        <rect
          x="172"
          y="96"
          width="96"
          height="52"
          fill="none"
          stroke="#2f4a38"
          strokeWidth="1.2"
        />
        <text x="190" y="126">
          {nl.praktisch.gebouw.plan.sanitair}
        </text>
        <rect
          x="172"
          y="148"
          width="96"
          height="38"
          fill="none"
          stroke="#2f4a38"
          strokeWidth="1.2"
        />
        <text x="204" y="171">
          {nl.praktisch.gebouw.plan.trap}
        </text>
        <g stroke="#2f4a38" strokeWidth="1.4" strokeDasharray="4 4">
          <rect x="296" y="26" width="132" height="160" fill="none" />
          <line x1="296" y1="106" x2="428" y2="106" />
        </g>
        <text x="304" y="18" fill="#8a8672">
          {nl.praktisch.gebouw.plan.verdieping}
        </text>
        <text x="312" y="62" fontWeight="600">
          {nl.praktisch.gebouw.plan.slaapzaalBoven}
        </text>
        <text x="330" y="76">
          {nl.praktisch.gebouw.plan.bedden(32)}
        </text>
        <text x="310" y="142" fontWeight="600">
          {nl.praktisch.gebouw.plan.slaapzaalZolder}
        </text>
        <text x="330" y="156">
          {nl.praktisch.gebouw.plan.bedden(28)}
        </text>
        <text x="20" y="18" fill="#8a8672">
          {nl.praktisch.gebouw.plan.gelijkvloers}
        </text>
        <text
          x="220"
          y="112"
          fontSize="26"
          fontWeight="700"
          fill="#d93b1c"
          fillOpacity="0.28"
          textAnchor="middle"
          transform="rotate(-8 220 112)"
        >
          {nl.praktisch.gebouw.plan.placeholder}
        </text>
      </g>
    </svg>
  );
}

function Kaart() {
  return (
    <iframe
      src={MAPS_EMBED_URL}
      title={nl.praktisch.ligging.kaartTitel}
      loading="lazy"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      className="h-72 w-full rounded-lg border-0"
    />
  );
}

/** Het gebouw, de ligging en de veelgestelde vragen — static content. */
export function PraktischPage() {
  return (
    <div>
      <h1 className="squiggle font-bold font-display text-3xl tracking-tight">
        {nl.praktisch.titel}
      </h1>
      <div className="mt-8 grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-2">
        <section>
          <h2 className="mb-3 font-bold font-display text-lg">{nl.praktisch.gebouw.titel}</h2>
          <div className="rounded-xl border-2 border-lijn bg-kaart p-3">
            <Plattegrond />
            <p className="px-1 pt-1 pb-1 text-mist text-xs">
              {nl.praktisch.gebouw.plattegrondNoot}
            </p>
          </div>
          <ul className="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
            {nl.praktisch.gebouw.troeven.map((troef) => (
              <li key={troef} className="relative pl-4">
                <i className="absolute top-1.5 left-0 block h-1.5 w-1.5 rounded-sm bg-rood" />
                {troef}
              </li>
            ))}
          </ul>
          <p className="mt-2 font-semibold text-[11.5px] text-amber">
            {nl.praktisch.gebouw.troevenNoot}
          </p>

          <h2 className="mt-8 mb-3 font-bold font-display text-lg">
            {nl.praktisch.gebouw.fotos.titel}
          </h2>
          {/* Obvious placeholders: real photos drop straight into this grid. */}
          <div className="grid grid-cols-2 gap-3">
            {nl.praktisch.gebouw.fotos.tegels.map((tegel, index) => (
              <figure
                key={tegel}
                className={`flex aspect-[4/3] flex-col items-center justify-center gap-2 rounded-xl border-2 border-lijn border-dashed ${
                  index % 2 === 0 ? "bg-zand/60" : "hatch"
                }`}
              >
                <span className="opacity-50">
                  <TentMark className="size-8" />
                </span>
                <figcaption className="px-3 text-center">
                  <span className="block font-semibold text-[13px]">{tegel}</span>
                  <span className="font-mono text-[10px] text-rood uppercase tracking-widest">
                    {nl.praktisch.gebouw.fotos.volgt}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-3 font-bold font-display text-lg">{nl.praktisch.ligging.titel}</h2>
          <div className="rounded-xl border-2 border-lijn bg-kaart p-3">
            <Kaart />
            <p className="px-1 pt-2 pb-1 text-mist text-xs">
              {nl.praktisch.ligging.adres} · {nl.praktisch.ligging.noot}{" "}
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-rood"
              >
                {nl.praktisch.ligging.opKaart}
              </a>
            </p>
          </div>
        </section>

        <section>
          <h2 className="mb-1 font-bold font-display text-lg">{nl.praktisch.faq.titel}</h2>
          {nl.praktisch.faq.vragen.map((item) => (
            <details key={item.vraag} className="group border-lijn border-b py-3">
              <summary className="flex min-h-9 cursor-pointer list-none items-center justify-between gap-4 font-semibold text-sm [&::-webkit-details-marker]:hidden">
                {item.vraag}
                <span className="font-mono font-semibold text-rood group-open:hidden">+</span>
                <span className="hidden font-mono font-semibold text-rood group-open:inline">
                  –
                </span>
              </summary>
              <p className="max-w-lg pt-2 text-mist text-sm leading-relaxed">{item.antwoord}</p>
            </details>
          ))}
        </section>
      </div>
    </div>
  );
}
